import type { UseTimelineNewActivityIndicatorParams } from '../../types/timeline/new-activity-indicator.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getActivityTimestamp } from 'src/modules/communications/utils/timeline/activity-timestamp.util';
import type { ActivityTimestamp } from '../../types/timeline/activity-timestamp.types';

/**
 * Tracks new timeline activity while the user is away from the newest item.
 * @param params - Displayed items, current scroll position and scroll action.
 * @returns The unread-activity flag and an action that clears it and scrolls down.
 */
export const useTimelineNewActivityIndicator = ({
  items,
  isAtBottom,
  scrollToBottom,
}: UseTimelineNewActivityIndicatorParams) => {
  const latestActivityToken = useMemo(() => {
    const latestActivity = items.reduce<{
      token: string;
      timestamp: number;
    } | null>((latest, item) => {
      const timestampValue = [
        item.updatedAt,
        item.createdAt,
        'changedAt' in item ? item.changedAt : undefined,
        'startTime' in item ? item.startTime : undefined,
      ]
        .map(getActivityTimestamp)
        .find((value): value is ActivityTimestamp => value !== null);
      const timestamp = timestampValue?.timestamp ?? 0;

      if (latest && latest.timestamp >= timestamp) {
        return latest;
      }

      return {
        token: `${item.modelType}:${item.id}:${timestampValue?.tokenValue ?? ''}`,
        timestamp,
      };
    }, null);

    return latestActivity?.token ?? null;
  }, [items]);

  const latestActivityTokenRef = useRef<string | null>(null);
  const [hasNewActivity, setHasNewActivity] = useState(false);

  useEffect(() => {
    if (!latestActivityToken) {
      return;
    }

    if (
      latestActivityTokenRef.current &&
      latestActivityTokenRef.current !== latestActivityToken &&
      !isAtBottom
    ) {
      setHasNewActivity(true);
    }

    latestActivityTokenRef.current = latestActivityToken;
  }, [isAtBottom, latestActivityToken]);

  useEffect(() => {
    if (isAtBottom) {
      setHasNewActivity(false);
    }
  }, [isAtBottom]);

  const handleScrollToBottom = useCallback(() => {
    setHasNewActivity(false);
    scrollToBottom();
  }, [scrollToBottom]);

  return {
    hasNewActivity,
    handleScrollToBottom,
  };
};
